import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import {
  ChevronsRightIcon,
  Edit,
  Minus,
  Plus,
  Trash,
  Download,
} from "lucide-react";
import {
  AddButton,
  CustomConfirmModal,
  PaginationDropdown,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Pagination,
} from "@mui/material";
import { toast } from "react-toastify";
import Loader from "../../../components/partials/Loader";
import * as XLSX from "xlsx";

import GeneralFooter from "../../common/GeneralFooter";
import PermissionButton from "../../common/PermissionButton";
const FeatureSubTypeList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [loader, setLoader] = useState(true);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fetchSubTypes = async () => {
    try {
      let statusFilter = undefined;
      if (search === "active" || search === "ac") {
        statusFilter = "1";
      } else if (search === "inactive" || search === "ina") {
        statusFilter = "0";
      }

      const response = await fetchData(
        "subtypes",
        "GET",
        null,
        {},
        {
          search: statusFilter || search,
          page: pagination.currentPage,
          limit: pagination.pageSize,
        },
        navigate,
        "/feature-types",
        dispatch
      );

      setLoader(false);
      setData(response?.data || []);
      setPagination(response?.pagination || {});
    } catch (error) {
      setLoader(false);
      toast.error("Failed to fetch feature sub types.");
    }
  };

  useEffect(() => {
    fetchSubTypes();
  }, [pagination.currentPage, pagination.pageSize, search]);

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const handleDelete = async () => {
    setModalVisible(false);
    try {
      await fetchData(
        "subtypes",
        "DELETE",
        { id: selectedId },
        {},
        {},
        navigate,
        "/featuretypes",
        dispatch
      );
      toast.success("Feature Sub Type deleted successfully.");
      fetchSubTypes();
    } catch (error) {
      toast.error("Failed to delete feature sub type.");
    }
  };

  const showDeleteModal = (id) => {
    setSelectedId(id);
    setModalVisible(true);
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handleSearchChange = (e) => {
    const input = e.target.value.toLowerCase();
    if (input === "active" || input === "inactive") {
      setSearch(input);
    } else {
      setSearch(input);
    }
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };
  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      let statusFilter = undefined;
      if (search === "active" || search === "ac") {
        statusFilter = "1";
      } else if (search === "inactive" || search === "ina") {
        statusFilter = "0";
      }
      const response = await fetchData(
        "subtypes",
        "GET",
        null,
        {},
        {
          search: statusFilter || search,
          limit: 999999,
          page: 1,
        },
        navigate,
        "/feature-types",
        dispatch
      );

      const exportData = response?.data || [];
      const excelData = exportData.map((subType, index) => ({
        "Sr.No": index + 1,
        "Sub Type Name": subType.feature_sub_type || "-",
        Feature: subType.feature?.feature_name || "N/A",
        Status: subType.status == "1" ? "Active" : "In-Active",
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const columnWidths = [{ wch: 8 }, { wch: 25 }, { wch: 20 }, { wch: 12 }];
      worksheet["!cols"] = columnWidths;
      XLSX.utils.book_append_sheet(workbook, worksheet, "Feature Sub Types");
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `Feature_SubTypes_Export_${currentDate}.xlsx`;
      XLSX.writeFile(workbook, filename);

      toast.success(`Excel file exported successfully: ${filename}`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    {
      name: <span className="text-[0.813rem]">Sr.No</span>,
      cell: (row, index) => {
        const currentPage = pagination?.currentPage || 1;
        const pageSize = pagination?.pageSize || 10;

        return <span>{(currentPage - 1) * pageSize + index + 1}</span>;
      },
      width: "80px",
    },
    {
      name: <span className="text-[0.813rem]">Sub Type Name</span>,
      selector: (row) => row.feature_sub_type || "N/A",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Feature</span>,
      selector: (row) => row.feature?.feature_name || "N/A",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Status</span>,
      cell: (row) => {
        const isActive = row.status == "1";
        return (
          <span
            className={`py-1 px-2 text-[0.85em] rounded ${
              isActive ? "bg-success text-white" : "bg-red-500 text-white"
            }`}
          >
            {isActive ? "Active" : "In-Active"}
          </span>
        );
      },
    },
  ];
  {
    if (
      permissions.includes("feature-sub-type-update") ||
      permissions.includes("feature-sub-type-delete")
    ) {
      columns.push({
        name: <span className="text-[0.813rem]">Action</span>,
        cell: (row) => (
          <div className="flex gap-2">
            {/* <div className="flex gap-2">
          <Link to={`/subtypes/edit/${row.feature_sub_type_id}`}>
            <i className="fa-solid fa-pen-to-square fa-lg"></i>
          </Link>
        </div>
        <button onClick={() => showDeleteModal(row.feature_sub_type_id)}>
          <i
            class="fa fa-trash fa-lg text-primaryDark"
            aria-hidden="true"
          ></i>
        </button> */}

            <PermissionButton
              hasPermission={permissions.includes("feature-sub-type-update")}
              to={`/subtypes/edit/${row.feature_sub_type_id}`}
              faIcon="fa-solid fa-pen-to-square fa-lg"
              onlyIcon={true}
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed"
            />
            <PermissionButton
              hasPermission={permissions.includes("feature-sub-type-delete")}
              faIcon="fa fa-trash fa-lg text-primaryDark"
              onlyIcon
              onClick={() => showDeleteModal(row.feature_sub_type_id)}
              className="p-1 rounded-full bg-gray-200 text-[#137e81] hover:text-white"
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed bg-gray-300"
            />

            <CustomConfirmModal
              isVisible={isModalVisible}
              onConfirm={handleDelete}
              onCancel={() => setModalVisible(false)}
              message="Are you sure you want to delete this Feature Sub Type?"
            />
          </div>
        ),
      });
    }
  }

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  useEffect(() => {
    setTimeout(() => setLoader(false), 2000);
  }, []);

  return (
    <div className="w-full">
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex  lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            Feature Sub Type
          </h1>
          <div className="flex gap-2">
            {data.length > 0 && (
              <button
                onClick={exportToExcel}
                disabled={isExporting}
                className={`flex items-center gap-2 px-4 py-1 text-sm bg-[#137e81] hover:bg-green-700 text-white rounded-md transition-colors ${
                  isExporting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Download size={16} />
                {isExporting ? "Exporting..." : "Export Excel"}
              </button>
            )}
            {/* <Link to="add">
              <AddButton text="Add" icon={ChevronsRightIcon} />
            </Link> */}
            <PermissionButton
              hasPermission={permissions.includes("feature-sub-type-create")}
              to="add"
              faIcon="fa fa-plus"
              text="Add"
              className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px]"
              enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
              disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      {/* <Accordion>
        <AccordionSummary
          expandIcon={
            isFiltersVisible ? (
              <Minus size={14} color="#fff" />
            ) : (
              <Plus size={14} color="#fff" />
            )
          }
          onClick={() => setIsFiltersVisible((prev) => !prev)}
          sx={{
            backgroundColor: "#137e81",
            color: "#fff !important", // MUI sx prop with !important
            "& .MuiAccordionSummary-content": {
              color: "#fff  !important", // Target the content specifically
            },
          }}
        >
          Filters
        </AccordionSummary>
        <AccordionDetails>
          <input
            type="text"
            placeholder="Search Sub Type / Feature / Status"
            className="text-[Light] flex items-center bg-[#ebeef2] border-[#ebeef2] rounded-md px-3 py-1 outline-none"
            value={search}
            onChange={handleSearchChange}
          />
        </AccordionDetails>
      </Accordion> */}

      <br />
      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4  mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
          <h1 className="lg:text-[0.875rem] text-[13px]  tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Feature Sub Type List
          </h1>
        </div>
        <div className="p-[1.25rem] w-full">
          <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-4 w-full">
            <PaginationDropdown
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              options={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
              ]}
            />

            <input
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              type="search"
              className="text-[Light] flex items-center bg-[#ebeef2] border-[#ebeef2] rounded-md px-3 py-1 outline-none"
            />
          </div>

          <DataTable
            columns={columns}
            persistTableHead
            data={data}
            customStyles={customStyles}
            highlightOnHover
          />
          <GeneralFooter
            pagination={pagination}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            dataLength={data.length}
          />
        </div>
      </div>
    </div>
  );
};

export default FeatureSubTypeList;
